import { useEffect } from 'react';

interface WebVitalMetric {
  name: string;
  value: number;
  id: string;
  delta: number;
}

interface LayoutShiftEntry extends PerformanceEntry {
  value: number;
  hadRecentInput: boolean;
}

interface FirstInputEntry extends PerformanceEntry {
  processingStart: number;
}

const useWebVitals = () => {
  useEffect(() => {
    // Nur im Browser und nicht im Development-Modus (falls definiert)
    if (typeof window === 'undefined') {
      return;
    }

    const reportWebVital = (metric: WebVitalMetric) => {
      console.log('Web Vital:', metric.name, metric.value);
      
      // In einem echten Projekt würde man diese Metriken an einen Analytics-Service senden
      // Beispiel für Google Analytics 4:
      // gtag('event', metric.name, {
      //   value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
      //   event_category: 'Web Vitals',
      //   event_label: metric.id,
      //   non_interaction: true,
      // });
    };

    // Dynamischer Import der web-vitals Bibliothek (würde separat installiert werden)
    // Für diese Demo simulieren wir einfache Performance-Messungen
    
    // Largest Contentful Paint (LCP) - Zeit bis zum größten sichtbaren Element
    const measureLCP = () => {
      if ('PerformanceObserver' in window) {
        const observer = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          const lastEntry = entries[entries.length - 1];
          
          reportWebVital({
            name: 'LCP',
            value: lastEntry.startTime,
            id: 'lcp-' + Date.now(),
            delta: lastEntry.startTime
          });
        });
        
        try {
          observer.observe({ entryTypes: ['largest-contentful-paint'] });
        } catch {
          // Browser unterstützt LCP nicht
          console.log('LCP nicht unterstützt');
        }
      }
    };

    // First Input Delay (FID) - Zeit bis zur ersten Interaktion
    const measureFID = () => {
      if ('PerformanceObserver' in window) {
        const observer = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          entries.forEach((entry) => {
            const fidEntry = entry as FirstInputEntry;
            reportWebVital({
              name: 'FID',
              value: fidEntry.processingStart - fidEntry.startTime,
              id: 'fid-' + Date.now(),
              delta: fidEntry.processingStart - fidEntry.startTime
            });
          });
        });
        
        try {
          observer.observe({ entryTypes: ['first-input'] });
        } catch {
          console.log('FID nicht unterstützt');
        }
      }
    };

    // Cumulative Layout Shift (CLS) - Visuelle Stabilität
    const measureCLS = () => {
      if ('PerformanceObserver' in window) {
        let clsValue = 0;
        
        const observer = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          entries.forEach((entry) => {
            const clsEntry = entry as LayoutShiftEntry;
            if (!clsEntry.hadRecentInput) {
              clsValue += clsEntry.value;
            }
          });
          
          reportWebVital({
            name: 'CLS',
            value: clsValue,
            id: 'cls-' + Date.now(),
            delta: clsValue
          });
        });
        
        try {
          observer.observe({ entryTypes: ['layout-shift'] });
        } catch {
          console.log('CLS nicht unterstützt');
        }
      }
    };

    // Time to First Byte (TTFB) - Server-Response-Zeit
    const measureTTFB = () => {
      if ('performance' in window && 'getEntriesByType' in performance) {
        const navigationEntries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
        if (navigationEntries.length > 0) {
          const ttfb = navigationEntries[0].responseStart - navigationEntries[0].requestStart;
          
          reportWebVital({
            name: 'TTFB',
            value: ttfb,
            id: 'ttfb-' + Date.now(),
            delta: ttfb
          });
        }
      }
    };

    // Alle Metriken messen
    measureLCP();
    measureFID();
    measureCLS();
    measureTTFB();

    // Cleanup-Funktion
    return () => {
      // Observer würden hier cleanup werden
    };
  }, []);
};

export default useWebVitals;
